import { s3UploadService } from './s3UploadService';
import { loggingService } from '../utils/loggingService';

// Upload result interface
interface UploadResult {
  success: boolean;
  s3Key?: string;
  downloadUrl?: string;
  publicUrl?: string;
  error?: string;
  uploadMethod?: string;
}

// Network connection info for diagnostics (Navigator.connection API)
interface NetworkInfo {
  type?: string;         // e.g., 'wifi', 'cellular', '4g'
  effectiveType?: string; // e.g., '4g', '3g', '2g', 'slow-2g'
  downlink?: number;      // Mbps
  rtt?: number;           // ms
  saveData?: boolean;
  isOnline: boolean;
  platform: string;
  isIPhone: boolean;
  isSafari: boolean;
}

/**
 * Get network diagnostics for debugging upload issues (especially on iPhone)
 */
function getNetworkDiagnostics(): NetworkInfo {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const conn = typeof navigator !== 'undefined' ? (navigator as any).connection : undefined;

  return {
    type: conn?.type,
    effectiveType: conn?.effectiveType,
    downlink: conn?.downlink,
    rtt: conn?.rtt,
    saveData: conn?.saveData,
    isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
    platform: typeof navigator !== 'undefined' ? ((navigator as any).userAgentData?.platform || navigator.userAgent) : 'unknown',
    isIPhone: ua.includes('iPhone'),
    isSafari: ua.includes('Safari') && !ua.includes('Chrome'),
  };
}

/**
 * Hybrid Upload Service - S3 direct upload
 *
 * Previously attempted Cloudinary first with S3 as fallback. Now uploads
 * directly to S3 via presigned URLs. The public API (exported function names
 * and return shape) is unchanged so callers do not need to be updated.
 *
 * IMPORTANT: The `s3Key` field in the result is the permanent identifier
 * that must be stored in the database. The `downloadUrl` field is a short-
 * lived presigned URL for immediate display only — do NOT persist it.
 */
export class HybridUploadService {
  /**
   * Upload a file using S3 presigned URLs.
   * @param file The file to upload
   * @param folder Optional folder to organize uploads
   * @returns Upload result with s3Key (permanent) and downloadUrl (temporary display)
   */
  async uploadFile(file: File, folder: string = 'reports'): Promise<UploadResult> {
    const networkInfo = getNetworkDiagnostics();
    const uploadStartTime = Date.now();
    console.log(`Starting S3 upload for file: ${file.name} (${Math.round(file.size / 1024)}KB)`, { networkInfo });

    loggingService.logUserAction('HybridUpload_Start', {
      fileName: file.name,
      fileSize: file.size,
      fileSizeKB: Math.round(file.size / 1024),
      fileSizeMB: Math.round(file.size / 1024 / 1024 * 100) / 100,
      folder,
      network: networkInfo,
      timestamp: new Date().toISOString()
    });

    try {
      const s3Result = await s3UploadService.uploadFile(file, folder);

      if (s3Result.success && s3Result.s3Key) {
        const durationMs = Date.now() - uploadStartTime;
        console.log('S3 upload successful!', { durationMs, s3Key: s3Result.s3Key });

        loggingService.logUserAction('HybridUpload_S3Success', {
          fileName: file.name,
          fileSize: file.size,
          fileSizeKB: Math.round(file.size / 1024),
          fileSizeMB: Math.round(file.size / 1024 / 1024 * 100) / 100,
          folder,
          s3Key: s3Result.s3Key,
          durationMs,
          network: networkInfo,
          timestamp: new Date().toISOString()
        }, durationMs);

        return {
          ...s3Result,
          uploadMethod: 's3'
        };
      } else {
        const errorMessage = s3Result.error || 'S3 upload failed';
        const totalDurationMs = Date.now() - uploadStartTime;
        console.error('S3 upload failed:', { error: errorMessage, networkInfo });

        loggingService.logUserAction('HybridUpload_S3Failed', {
          fileName: file.name,
          fileSize: file.size,
          error: errorMessage,
          totalDurationMs,
          network: networkInfo,
          timestamp: new Date().toISOString()
        }, totalDurationMs, false);

        return {
          success: false,
          error: errorMessage,
          uploadMethod: 's3-failed'
        };
      }
    } catch (s3Error: any) {
      const totalDurationMs = Date.now() - uploadStartTime;
      const errorMessage = s3Error?.message || 'S3 upload exception';
      console.error('S3 upload exception:', { error: errorMessage, networkInfo });

      loggingService.logUserAction('HybridUpload_S3Exception', {
        fileName: file.name,
        fileSize: file.size,
        error: errorMessage,
        totalDurationMs,
        network: networkInfo,
        timestamp: new Date().toISOString()
      }, totalDurationMs, false);

      return {
        success: false,
        error: errorMessage,
        uploadMethod: 's3-exception'
      };
    }
  }

  /**
   * Upload base64 data using S3 presigned URLs.
   * @param base64Data The base64 image data
   * @param fileName The file name for the upload
   * @param folder Optional folder to organize uploads
   * @returns Upload result with s3Key (permanent) and downloadUrl (temporary display)
   */
  async uploadBase64(base64Data: string, fileName: string, folder: string = 'reports'): Promise<UploadResult> {
    console.log(`Starting S3 base64 upload for file: ${fileName}`);

    const estimatedSize = (base64Data.length * 3) / 4;

    loggingService.logUserAction('HybridUpload_Start', {
      fileName: fileName,
      fileSize: estimatedSize,
      fileSizeKB: Math.round(estimatedSize / 1024),
      fileSizeMB: Math.round(estimatedSize / 1024 / 1024 * 100) / 100,
      folder,
      uploadMethod: 'base64',
      timestamp: new Date().toISOString()
    });

    try {
      const s3Result = await s3UploadService.uploadBase64Image(base64Data, fileName, folder);

      if (s3Result.success && s3Result.s3Key) {
        console.log('S3 base64 upload successful!', { s3Key: s3Result.s3Key });

        loggingService.logUserAction('HybridUpload_S3Success', {
          fileName: fileName,
          fileSize: estimatedSize,
          fileSizeKB: Math.round(estimatedSize / 1024),
          fileSizeMB: Math.round(estimatedSize / 1024 / 1024 * 100) / 100,
          folder,
          s3Key: s3Result.s3Key,
          uploadMethod: 'base64',
          timestamp: new Date().toISOString()
        });

        return {
          ...s3Result,
          uploadMethod: 's3-base64'
        };
      } else {
        const errorMessage = s3Result.error || 'S3 base64 upload failed';
        console.error('S3 base64 upload failed:', errorMessage);

        loggingService.logUserAction('HybridUpload_Base64UploadFailed', {
          fileName: fileName,
          fileSize: estimatedSize,
          fileSizeKB: Math.round(estimatedSize / 1024),
          fileSizeMB: Math.round(estimatedSize / 1024 / 1024 * 100) / 100,
          folder,
          error: errorMessage,
          uploadMethod: 'base64',
          timestamp: new Date().toISOString()
        });

        return {
          success: false,
          error: errorMessage,
          uploadMethod: 'base64-failed'
        };
      }
    } catch (error: any) {
      const errorMessage = error?.message || String(error);
      console.error('S3 base64 upload exception:', errorMessage);

      loggingService.logUserAction('HybridUpload_Base64UploadFailed', {
        fileName: fileName,
        fileSize: estimatedSize,
        error: errorMessage,
        uploadMethod: 'base64',
        timestamp: new Date().toISOString()
      });

      return {
        success: false,
        error: errorMessage,
        uploadMethod: 'base64-failed'
      };
    }
  }

  /**
   * Upload a blob using S3 presigned URLs.
   * The blob is converted to a File object internally before uploading.
   * @param blob The blob to upload
   * @param fileName The file name for the upload
   * @param folder Optional folder to organize uploads
   * @returns Upload result with s3Key (permanent) and downloadUrl (temporary display)
   */
  async uploadBlob(blob: Blob, fileName: string, folder: string = 'reports'): Promise<UploadResult> {
    console.log(`Starting S3 blob upload for file: ${fileName} (${Math.round(blob.size / 1024)}KB)`);

    loggingService.logUserAction('HybridUpload_Start', {
      fileName: fileName,
      fileSize: blob.size,
      fileSizeKB: Math.round(blob.size / 1024),
      fileSizeMB: Math.round(blob.size / 1024 / 1024 * 100) / 100,
      folder,
      uploadMethod: 'blob',
      timestamp: new Date().toISOString()
    });

    try {
      // Convert Blob to File so s3UploadService.uploadFile() can use it
      const file = new File([blob], fileName, { type: blob.type || 'image/jpeg' });
      const s3Result = await s3UploadService.uploadFile(file, folder);

      if (s3Result.success && s3Result.s3Key) {
        console.log('S3 blob upload successful!', { s3Key: s3Result.s3Key });

        loggingService.logUserAction('HybridUpload_S3Success', {
          fileName: fileName,
          fileSize: blob.size,
          fileSizeKB: Math.round(blob.size / 1024),
          fileSizeMB: Math.round(blob.size / 1024 / 1024 * 100) / 100,
          folder,
          s3Key: s3Result.s3Key,
          uploadMethod: 'blob',
          timestamp: new Date().toISOString()
        });

        return {
          ...s3Result,
          uploadMethod: 's3-blob'
        };
      } else {
        const errorMessage = s3Result.error || 'S3 blob upload failed';
        console.error('S3 blob upload failed:', errorMessage);

        loggingService.logUserAction('HybridUpload_BlobUploadFailed', {
          fileName: fileName,
          fileSize: blob.size,
          fileSizeKB: Math.round(blob.size / 1024),
          fileSizeMB: Math.round(blob.size / 1024 / 1024 * 100) / 100,
          folder,
          error: errorMessage,
          uploadMethod: 'blob',
          timestamp: new Date().toISOString()
        });

        return {
          success: false,
          error: errorMessage,
          uploadMethod: 'blob-failed'
        };
      }
    } catch (error: any) {
      const errorMessage = error?.message || String(error);
      console.error('S3 blob upload exception:', errorMessage);

      loggingService.logUserAction('HybridUpload_BlobUploadFailed', {
        fileName: fileName,
        fileSize: blob.size,
        error: errorMessage,
        uploadMethod: 'blob',
        timestamp: new Date().toISOString()
      });

      return {
        success: false,
        error: errorMessage,
        uploadMethod: 'blob-exception'
      };
    }
  }

  /**
   * Get the status of the upload service.
   * @returns Configuration status for debugging
   */
  getUploadStatus() {
    return {
      s3: {
        available: !!s3UploadService,
        service: 's3UploadService'
      }
    };
  }

  /**
   * Test upload capabilities without actually uploading.
   * @returns Status of upload services
   */
  async testUploadCapabilities(): Promise<{
    s3: { available: boolean; error?: string };
  }> {
    const result = {
      s3: { available: false, error: undefined as string | undefined }
    };

    try {
      result.s3.available = !!s3UploadService;
      if (!result.s3.available) {
        result.s3.error = 'S3 service not available';
      }
    } catch (error: any) {
      result.s3.error = error?.message || 'S3 service error';
    }

    console.log('Upload capabilities test:', result);
    return result;
  }
}

// Create a default instance
export const hybridUploadService = new HybridUploadService();

export default hybridUploadService;
